import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaEdit, FaTrashAlt } from 'react-icons/fa';
import { getBaseUrl } from "../global/Globalapi";
const CompanyMasterForm = () => {
  const [formData, setFormData] = useState({
    company_code: '',
    company_name: '',
    company_email: '',
    company_phone: '',
    company_address: '',
    gst_no: '',
    status: 'active'
  });

  const [companies, setCompanies] = useState([]);
  const [editId, setEditId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  // Backend URL on port 5000
  // const API_BASE_URL = 'http://localhost:5000';
  const API_BASE_URL = getBaseUrl();

  // Fetch companies on component mount
  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/companymaster`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      let response;
      
      if (editId) {
        // Update existing company
        response = await axios.put(`${API_BASE_URL}/api/companymaster/${editId}`, formData, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000
        });
        setMessage({ type: 'success', text: 'Company updated successfully!' });
      } else {
        // Create new company
        response = await axios.post(`${API_BASE_URL}/api/companymaster`, formData, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000
        });
        setMessage({ type: 'success', text: 'Company saved successfully!' });
      }

      console.log('Response:', response.data);
      handleReset();
      fetchCompanies(); // Refresh the list
    } catch (error) {
      console.error('Error details:', error);
      
      if (error.code === 'ECONNABORTED') {
        setMessage({ type: 'error', text: 'Request timeout - server not responding' });
      } else if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        
        const errorMsg = error.response.data?.message || 
                         error.response.data?.error || 
                         `Server error: ${error.response.status}`;
        setMessage({ type: 'error', text: errorMsg });
      } else if (error.request) {
        console.error('No response received:', error.request);
        setMessage({ type: 'error', text: 'No response from server. Check if backend is running on port 5000.' });
      } else {
        console.error('Request setup error:', error.message);
        setMessage({ type: 'error', text: `Error: ${error.message}` });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (company) => {
    setEditId(company.company_id);
    setFormData({
      company_code: company.company_code || '',
      company_name: company.company_name || '',
      company_email: company.company_email || '',
      company_phone: company.company_phone || '',
      company_address: company.company_address || '',
      gst_no: company.gst_no || '',
      status: company.status || 'active'
    });
    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/api/companymaster/${companyId}`);
      setMessage({ type: 'success', text: 'Company deleted successfully!' });
      fetchCompanies(); // Refresh the list
    } catch (error) {
      console.error('Error deleting company:', error);
      const errorMsg = error.response?.data?.message || 'Error deleting company';
      setMessage({ type: 'error', text: errorMsg });
    }
  };

  const handleReset = () => {
    setEditId(null);
    setFormData({
      company_code: '',
      company_name: '',
      company_email: '',
      company_phone: '',
      company_address: '',
      gst_no: '',
      status: 'active'
    });
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    margin: '8px 0 15px 0',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '15px',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    fontWeight: '600',
    display: 'block',
    marginBottom: '5px',
    color: '#555',
    fontSize: '14px'
  };

  const requiredStar = {
    color: 'red',
    marginLeft: '4px'
  };

  return (
    <div style={{ maxWidth: '900px', margin: '50px auto', padding: '20px' }}>
      {/* Form Section */}
      <div style={{
        padding: '30px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        backgroundColor: '#f9f9f9',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        marginBottom: '30px'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          marginBottom: '30px', 
          color: '#333',
          borderBottom: '2px solid #4CAF50',
          paddingBottom: '10px'
        }}>
          {editId ? 'Edit Company' : 'Add Company'}
        </h2>
      
        {message.text && (
          <div style={{
            padding: '12px',
            marginBottom: '20px',
            borderRadius: '4px',
            backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
            color: message.type === 'success' ? '#155724' : '#721c24',
            border: message.type === 'success' ? '1px solid #c3e6cb' : '1px solid #f5c6cb',
            fontSize: '14px'
          }}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div>
            <label style={labelStyle}>
              Company Code <span style={requiredStar}>*</span>
            </label>
            <input
              type="text"
              name="company_code"
              value={formData.company_code}
              onChange={handleChange}
              style={inputStyle}
              placeholder="Enter company code"
              required
              maxLength="100"
            />
          </div>

          <div>
            <label style={labelStyle}>
              Company Name <span style={requiredStar}>*</span>
            </label>
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
            onChange={handleChange}
            style={inputStyle}
            placeholder="Enter company name"
            required
            maxLength="100"
          />
        </div>

        <div>
          <label style={labelStyle}>
            Company Email <span style={requiredStar}>*</span>
          </label>
          <input
            type="email"
            name="company_email"
            value={formData.company_email}
            onChange={handleChange}
            style={inputStyle}
            placeholder="Enter company email"
            required
            maxLength="100"
          />
        </div>

        <div>
          <label style={labelStyle}>
            Company Phone <span style={requiredStar}>*</span>
          </label>
          <input
            type="tel"
            name="company_phone"
            value={formData.company_phone}
            onChange={handleChange}
            style={inputStyle}
            placeholder="Enter company phone"
            required
            maxLength="100"
          />
        </div>

        <div>
          <label style={labelStyle}>
            Company Address <span style={requiredStar}>*</span>
          </label>
          <textarea
            name="company_address"
            value={formData.company_address}
            onChange={handleChange}
            style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
            placeholder="Enter company address"
            required
            maxLength="500"
          />
        </div>

        <div>
          <label style={labelStyle}>
            GST Number <span style={requiredStar}>*</span>
          </label>
          <input
            type="text"
            name="gst_no"
            value={formData.gst_no}
            onChange={handleChange}
            style={inputStyle}
            placeholder="Enter GST number"
            required
            maxLength="100"
          />
        </div>

        <div>
          <label style={labelStyle}>
            Status <span style={requiredStar}>*</span>
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            style={inputStyle}
            required
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        <div style={{ 
          marginTop: '30px', 
          textAlign: 'center',
          borderTop: '1px solid #eee',
          paddingTop: '20px'
        }}>
          <button 
            type="submit" 
            disabled={loading}
            style={{
              backgroundColor: loading ? '#ccc' : '#4CAF50',
              color: 'white',
              padding: '12px 30px',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              marginRight: '15px',
              fontWeight: '600',
              minWidth: '150px'
            }}
          >
            {loading ? 'Saving...' : (editId ? 'Update Company' : 'Save Company')}
          </button>
          <button 
            type="button" 
            style={{
              backgroundColor: '#f44336',
              color: 'white',
              padding: '12px 30px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              minWidth: '150px'
            }}
            onClick={handleReset}
            disabled={loading}
          >
            {editId ? 'Cancel' : 'Reset'}
          </button>
        </div>

        <p style={{
          fontSize: '12px',
          color: '#999',
          marginTop: '15px',
          textAlign: 'center'
        }}>
          Fields marked with <span style={{ color: 'red' }}>*</span> are required
        </p>
        </form>
      </div>

      {/* Company List Section */}
      <div style={{
        padding: '30px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        backgroundColor: '#fff',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ 
          marginBottom: '20px', 
          color: '#333',
          borderBottom: '2px solid #2196F3',
          paddingBottom: '10px'
        }}>
          Company List
        </h3>

        {companies.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666' }}>No companies found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#b91c1c', color: 'white' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>S.No</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Code</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Name</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Email</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Phone</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>GST No</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company, index) => (
                  <tr key={company.company_id} style={{ 
                    backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff',
                    borderBottom: '1px solid #ddd'
                  }}>
                    <td style={{ padding: '12px' }}>{index + 1}</td>
                    <td style={{ padding: '12px' }}>{company.company_code}</td>
                    <td style={{ padding: '12px' }}>{company.company_name}</td>
                    <td style={{ padding: '12px' }}>{company.company_email}</td>
                    <td style={{ padding: '12px' }}>{company.company_phone}</td>
                    <td style={{ padding: '12px' }}>{company.gst_no}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        backgroundColor: company.status === 'active' ? '#d4edda' : '#f8d7da',
                        color: company.status === 'active' ? '#155724' : '#721c24'
                      }}>
                        {company.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleEdit(company)}
                        style={{
                          backgroundColor: '#2196F3',
                          color: 'white',
                          border: 'none',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginRight: '8px'
                        }}
                        title="Edit Company"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(company.company_id)}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        title="Delete Company"
                      >
                        <FaTrashAlt />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyMasterForm;